import type { NextPage } from 'next'

import { CategoryView } from '@components/category'
import type { CustomFacetValue, ManufacturerFacet, ProductSearchResult } from '@model/product'
import * as R from 'ramda'
import { Helmet } from '@components/helmet'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isNotNil } from '@utils/simple-helper'
import type {
  FilterValue,
  GetBrandDataHandler,
  ICategoryFiltersContext,
  IsBrandFilterEnabledHandler,
  SetCategoryBannersHandler,
  SetCategoryDataHandler,
  SetSortingOption,
  ToggleBrandFilterHandler,
} from '@context/category-filters'
import { CategoryFiltersContext } from '@context/category-filters'
import type { ParsedUrlQuery } from 'querystring'
import type { SortOrder } from '@model/product/ProductSearchResult'
import type { CustomFacet } from '@model/product/CustomFacet'
import { getFacetTitle } from '@model/product/CustomFacet'
import { useTranslate } from '@hooks/useTranslation'
import type { ImagePageBanner } from '@api/content'
import type { CategoryTip } from '@model/category/CategoryTip'
import { unSlug } from '@utils/slugs'
import { getCategoryDetails } from '@utils/product/getCategoryDetails'
import type { CatalogFromContent } from '@api/content'
import ModalStateProvider from '@context/ModalContext'
import { stringOfCustomFacets } from '@components/category/CategoryView/CategoryView'
import { sessionStorageHelper } from '@utils/localStorageHelper'
import { useInitialData } from '@context/initialDataContext'
import TranslationsLoaderProvider from '@ui/components/TranslationLoader/TranslationsLoader'

const groupCatalogs = R.groupBy<CatalogFromContent>(R.prop('info'))

function routerQueryValueToArray(queryValue: ParsedUrlQuery[0]): string[] {
  if (!queryValue) {
    return []
  }

  if ((queryValue as string) && (queryValue as string)?.localeCompare) {
    return [queryValue as string]
  }

  return queryValue as string[]
}

function noEmptyArrayValues(obj: { [key: string]: any[] }): { [key: string]: any[] } {
  return Object.keys(obj)
    .map((key) => {
      if (obj[key]?.length > 0) {
        return key
      }

      return null
    })
    .filter((key) => !!key)
    .reduce((newObj, key) => ({ ...newObj, [key!]: obj[key!] }), {})
}

type NameAndDescription = Pick<CategoryTip, 'name' | 'description'>

const findCategoryNameAndDescription = (categories: CategoryTip[], categoryId: number): NameAndDescription => {
  const flatCategories: CategoryTip[] = []

  const extract = (categories: CategoryTip[]) => {
    categories?.forEach((cat: CategoryTip) => {
      flatCategories.push(cat)
      if (cat.children) {
        extract(cat.children)
      }
    })
  }
  extract(categories)

  if (!flatCategories.length) return { name: '', description: '' }
  try {
    return R.compose(
      R.pick(['name', 'description']),
      R.find<CategoryTip>(({ id }) => id === categoryId)
    )(flatCategories) as NameAndDescription
  } catch (e) {
    return { name: '', description: '' }
  }
}

interface CategoryPageProps {
  catalogs?: CatalogFromContent[]
  manufacturerId?: number
  manufacturerIds?: number[]
  manufacturerName?: string
  jsxData?: string
}

export const CategoryPage: NextPage<CategoryPageProps> = ({
  catalogs = [],
  manufacturerId,
  manufacturerIds,
  manufacturerName,
  jsxData,
}) => {
  const { events } = useRouter()
  const catalogGroups = groupCatalogs(catalogs)
  const router = useRouter()
  const isProductsPage = router.asPath.split('/').includes('products')
  const {
    initialData: { categories },
  } = useInitialData()
  const { slug, term, name, catalogId, ...restParams } = router.query
  const previousPath = useRef<string | string[] | undefined>()
  const pageIdentifier = useMemo(() => {
    if (slug) return slug
    if (term) return term

    return 'default'
  }, [slug, term])

  const id = typeof pageIdentifier === 'string' ? unSlug(pageIdentifier) : undefined
  const [brandsFilters, setBrandsFilters] = useState<FilterValue[]>([])

  const [minPrice, setMinPrice] = useState<number | null>(null)
  const [maxPrice, setMaxPrice] = useState<number | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const isUpdatingUrlRef = useRef(false)
  const lastSyncedUrlPricesRef = useRef<{ minPrice: number | null; maxPrice: number | null }>({
    minPrice: null,
    maxPrice: null,
  })

  useEffect(() => {
    const currentPath = slug || term || 'default'
    const pathChanged = previousPath.current !== undefined && previousPath.current !== currentPath

    if (pathChanged) {
      setMinPrice(null)
      setMaxPrice(null)
      setCustoms([])
      setBrandsFilters([])
      setIsInitializing(true)
      lastSyncedUrlPricesRef.current = { minPrice: null, maxPrice: null }

      if (router.isReady && (router.query.minPrice || router.query.maxPrice)) {
        updateRouterQuery().catch(() => {})
      }
    }

    previousPath.current = currentPath
  }, [slug, term, router])

  const searchTerm = routerQueryValueToArray(term)?.[0] || undefined

  const [category, setCategory] = useState<ProductSearchResult | null>(null)
  const [sorting, setSorting] = useState<SortOrder>(null)
  const [banners, setBanners] = useState<ImagePageBanner[]>([])

  const { minPrice: minAvailablePrice, maxPrice: maxAvailablePrice } = category ?? {}

  const encodeBrandFilter = (brand: ManufacturerFacet | Pick<ManufacturerFacet, 'id' | 'name'>): FilterValue => {
    const { id, name } = brand
    const query = `${name}-${id}`
    return {
      id: id.toString(),
      name,
      query,
    }
  }
  const decodeBrandFilter = (queryValueString: string): FilterValue => {
    const [name, id] = queryValueString.split('-')
    return {
      id,
      name,
      query: queryValueString,
    }
  }

  const routerQuery = router.query

  const isBrandFilterEnabled: IsBrandFilterEnabledHandler = (brand) => {
    const { id } = brand

    return !!brandsFilters.find((brand) => brand.id.toString() === id.toString())
  }

  const toggleBrandFilter: ToggleBrandFilterHandler = (brand) => {
    const { id, name } = brand

    const isEnabled = isBrandFilterEnabled(brand)

    if (isEnabled) {
      setBrandsFilters((prevBrandsFiltersState) => {
        const brands = prevBrandsFiltersState.filter((f) => f.id.toString() !== id.toString())
        const paramsBrands = brands.length
          ? noEmptyArrayValues({ brands: brands.map(({ query }) => query) })
          : { brands: [] }
        updateRouterQuery(paramsBrands).then()
        return brands
      })
    } else {
      setBrandsFilters((prevBrandsFiltersState) => {
        const brands = [...prevBrandsFiltersState, encodeBrandFilter({ id: parseInt(id.toString()), name })]
        const paramsBrands = noEmptyArrayValues({ brands: brands.map(({ query }) => query) })
        updateRouterQuery(paramsBrands).then()
        return brands
      })
    }
  }

  const [customs, setCustoms] = useState<FilterValue[]>([])

  const updateRouterQuery = useCallback(
    (newQueryParams: Record<string, string | string[] | undefined> = {}) => {
      setMinPrice(null)
      setMaxPrice(null)
      lastSyncedUrlPricesRef.current = { minPrice: null, maxPrice: null }

      isUpdatingUrlRef.current = true

      const {
        minPrice: _minPrice,
        maxPrice: _maxPrice,
        page: _page,
        totalPages: _totalPages,
        ...restQuery
      } = router.query

      const query: Record<string, string | string[] | undefined> = {
        ...restQuery,
        ...newQueryParams,
      }

      delete query.minPrice
      delete query.maxPrice
      delete query.page
      delete query.totalPages

      Object.keys(query).forEach((key) => {
        const value = query[key]
        if (value === undefined || (Array.isArray(value) && value.length === 0)) {
          delete query[key]
        }
      })

      return router
        .replace(
          {
            pathname: router.pathname,
            query,
          },
          undefined,
          { scroll: false }
        )
        .then(() => {
          setTimeout(() => {
            isUpdatingUrlRef.current = false
          }, 300)
        })
        .catch(() => {
          isUpdatingUrlRef.current = false
        })
    },
    [router, setMinPrice, setMaxPrice]
  )

  const customFacets = category?.customFacets

  useEffect(() => {
    if (
      JSON.stringify(category?.customFacets) === JSON.stringify(sessionStorageHelper.get('customFacets')) ||
      !category?.customFacets
    )
      return

    sessionStorageHelper.set('customFacets', category?.customFacets)
  }, [category?.customFacets])

  const { translate } = useTranslate()
  const transformQueryName = (name: string) => {
    const replace = name.replace(/&&/g, '_')
    return replace.replace(/[^a-zA-Zа-яА-ЯёЁ0-9_]/g, '-')
  }

  useEffect(() => {
    if (!router.isReady || isUpdatingUrlRef.current) return
    const currentPath = slug || term || 'default'
    if (previousPath.current !== undefined && previousPath.current !== currentPath) {
      return
    }
    const minPriceFromQuery = router.query.minPrice
    const maxPriceFromQuery = router.query.maxPrice

    const urlMinPrice =
      minPriceFromQuery && typeof minPriceFromQuery === 'string' && !isNaN(parseFloat(minPriceFromQuery))
        ? parseFloat(minPriceFromQuery)
        : null
    const urlMaxPrice =
      maxPriceFromQuery && typeof maxPriceFromQuery === 'string' && !isNaN(parseFloat(maxPriceFromQuery))
        ? parseFloat(maxPriceFromQuery)
        : null

    if (isInitializing) {
      lastSyncedUrlPricesRef.current = { minPrice: urlMinPrice, maxPrice: urlMaxPrice }
      setMinPrice(urlMinPrice)
      setMaxPrice(urlMaxPrice)
      setIsInitializing(false)
    }
  }, [router.query.minPrice, router.query.maxPrice, router.isReady, isInitializing, minPrice, maxPrice])

  const updatePriceRangeInUrl = useCallback(
    (newMinPrice: number | null, newMaxPrice: number | null) => {
      if (!router.isReady || isInitializing) return

      const currentUrlMin = router.query.minPrice ? parseFloat(router.query.minPrice as string) : null
      const currentUrlMax = router.query.maxPrice ? parseFloat(router.query.maxPrice as string) : null

      if (currentUrlMin === newMinPrice && currentUrlMax === newMaxPrice) {
        return // No change needed
      }

      if (newMinPrice === null && newMaxPrice === null && (currentUrlMin !== null || currentUrlMax !== null)) {
        return // Skip clearing URL if we're trying to set null but URL has values
      }

      const { page, totalPages, ...restQuery } = router.query
      const newQuery: Record<string, string | string[] | undefined> = {
        ...restQuery,
      }

      if (newMinPrice === null) {
        delete newQuery.minPrice
      } else {
        newQuery.minPrice = newMinPrice.toString()
      }

      if (newMaxPrice === null) {
        delete newQuery.maxPrice
      } else {
        newQuery.maxPrice = newMaxPrice.toString()
      }

      lastSyncedUrlPricesRef.current = { minPrice: newMinPrice, maxPrice: newMaxPrice }
      isUpdatingUrlRef.current = true

      router
        .replace(
          {
            pathname: router.pathname,
            query: newQuery,
          },
          undefined,
          { scroll: false }
        )
        .then(() => {
          setTimeout(() => {
            isUpdatingUrlRef.current = false
          }, 100)
        })
        .catch(() => {
          isUpdatingUrlRef.current = false
        })
    },
    [router, isInitializing]
  )

  const debouncedUpdatePriceRangeInUrlRef = useRef<NodeJS.Timeout>()
  const debouncedUpdatePriceRangeInUrl = useCallback(
    (newMinPrice: number | null, newMaxPrice: number | null) => {
      if (debouncedUpdatePriceRangeInUrlRef.current) {
        clearTimeout(debouncedUpdatePriceRangeInUrlRef.current)
      }
      debouncedUpdatePriceRangeInUrlRef.current = setTimeout(() => {
        updatePriceRangeInUrl(newMinPrice, newMaxPrice)
      }, 500)
    },
    [updatePriceRangeInUrl]
  )

  const queryParams = useMemo(() => {
    const customFacetsParamsObject = noEmptyArrayValues(
      (customs ?? []).reduce(
        (params, facet) => ({
          ...params,
          [facet.name]: [...(params[facet.name as keyof typeof params] ?? []), facet.query],
        }),
        {}
      ) ?? {}
    )
    const paramsObject = noEmptyArrayValues(brandsFilters.length ? { brands: brandsFilters.map((b) => b.query) } : {})

    const minPriceObject = minPrice ? { minPrice } : {}
    const maxPriceObject = maxPrice ? { maxPrice } : {}
    const searchTermObject = searchTerm && !routerQueryValueToArray(id)?.[0] ? { searchTerm } : {}

    const urlParams = new URLSearchParams({
      ...paramsObject,
      ...customFacetsParamsObject,
      ...(sorting && { sorting }),
      ...minPriceObject,
      ...maxPriceObject,
      ...searchTermObject,
    } as any)
    urlParams.sort()

    return urlParams.toString()
  }, [brandsFilters, sorting, stringOfCustomFacets(customs), minPrice, maxPrice, searchTerm, id, name, pageIdentifier])

  const [customFiltersInitialized, setCustomFiltersInitialized] = useState(false)

  useEffect(() => {
    const brands = Array.isArray(router.query.brands) ? router.query.brands : [router.query.brands]
    if (brandsFilters.map((brand) => brand.query).join('') !== brands.join('')) {
      setBrandsFilters(routerQueryValueToArray(router.query.brands).map((b) => decodeBrandFilter(b)))
    }
  }, [router.query])

  useEffect(() => {
    const customFacetsSave = (sessionStorageHelper.get('customFacets') as CustomFacet[]) ?? customFacets
    if (customFiltersInitialized || !customFacetsSave) {
      return
    }

    setCustomFiltersInitialized(true)
    setCustoms(
      Object.keys(routerQuery)
        .filter((key) => key !== 'brands' && key !== 'id')
        .reduce((facets: FilterValue[], queryKey: string) => {
          return [
            ...facets,
            ...(routerQueryValueToArray(routerQuery[queryKey])
              .map((query) => {
                const name = queryKey
                const foundFacet = customFacetsSave?.find((f) => name === getFacetTitle(f.number))
                const foundFacetValue = foundFacet?.values?.find((v) => transformQueryName(v.name) === query)

                if (!foundFacet || !foundFacetValue) {
                  return null
                }

                return {
                  id: foundFacetValue.name,
                  group: foundFacet.number.toString(),
                  query,
                  name,
                } as FilterValue
              })
              .filter((f) => !!f) as FilterValue[]),
          ]
        }, [] as FilterValue[])
    )
  }, [customFiltersInitialized, routerQuery, customFacets, translate])

  const manufacturerFacets = category?.manufacturerFacets

  const brandsData = useMemo(() => {
    if (!manufacturerFacets?.length) {
      return []
    }

    return brandsFilters
      .map((b) => manufacturerFacets.find((m) => m.id.toString() === b.id.toString()))
      .filter((b) => !!b) as ManufacturerFacet[]
  }, [brandsFilters, manufacturerFacets])

  const getBrandData = useCallback<GetBrandDataHandler>(
    (brand) => {
      return manufacturerFacets?.find((m) => m.id.toString() === brand.id.toString()) ?? null
    },
    [manufacturerFacets]
  )

  if (category) {
    category.categoryFacets = category.categoryFacets?.filter(isNotNil)
    category.customFacets = category.customFacets?.filter(isNotNil)
    category.manufacturerFacets = category.manufacturerFacets?.filter(isNotNil)
  }

  const setCategoryData = useCallback<SetCategoryDataHandler>(setCategory, [])
  const setCategoryBanners = useCallback<SetCategoryBannersHandler>(setBanners, [])
  const setSortOrder = useCallback<SetSortingOption>(setSorting, [])

  useEffect(() => {
    if (!category?.manufacturerFacets?.length) {
      return
    }

    setBrandsFilters((prevBrandsFiltersState) => {
      const brands = prevBrandsFiltersState.filter(
        (v) => !!category?.manufacturerFacets.find((m) => m.id.toString() === v.id.toString())
      )
      const paramsBrands = brands.length
        ? noEmptyArrayValues({ brands: brands.map(({ query }) => query) })
        : { brands: [] }

      const currentBrandsLength = Array.isArray(router.query.brands)
        ? router.query.brands.length
        : router.query.brands
        ? 1
        : 0
      if (paramsBrands.brands.length < currentBrandsLength) {
        updateRouterQuery(paramsBrands).then()
      }

      return brands
    })
    setCustomFiltersInitialized(false)
  }, [category, router.query.brands, updateRouterQuery])

  const encodeCustomFilter = (facet: CustomFacet, value: CustomFacetValue): FilterValue => {
    const normalName = getFacetTitle(facet.number)
    const query = transformQueryName(value.name)

    return {
      id: value.name,
      name: normalName,
      group: facet.number.toString(),
      query,
    }
  }

  const decodeCustomFilter = (queryValueString: string): FilterValue => {
    const [name, id] = queryValueString.split('-')
    return {
      id,
      name,
      group: queryValueString,
      query: queryValueString,
    }
  }

  const isCustomFilterEnabled = (facet: FilterValue): boolean => {
    const value = facet
    if (value.id.split('&&').length) {
      return !!customs.find((f) => {
        return value.id.split('&&').includes(f.id) && f.group === value.group
      })
    }
    return !!customs.find((f) => f.group === value.group && f.id === value.id)
  }

  const setCustomFacetsQueryParams = (customs: FilterValue[]) => {
    return noEmptyArrayValues(
      (customs ?? []).reduce(
        (params, facet) => ({
          ...params,
          [facet.name]: [...(params[facet.name as keyof typeof params] ?? []), facet.query],
        }),
        {}
      ) ?? {}
    )
  }
  const toggleCustom = (facet: FilterValue) => {
    const value = facet
    const isEnabled = isCustomFilterEnabled(value)

    const group = value.id.split('&&')

    if (group.length > 1) {
      let copyCustoms = [...customs]
      group.forEach((item) => {
        const query = transformQueryName(item)
        const val = {
          id: item,
          name: facet.name,
          group: facet.group,
          query: query,
        }

        const isEnabled = isCustomFilterEnabled(val)

        if (isEnabled) {
          copyCustoms = copyCustoms.filter(
            (f) => f.id.toString() !== val.id.toString() && f.group?.toString() !== val.id.toString()
          )
        } else {
          copyCustoms.push(val)
        }
      })

      const customFacetsParamsObject = setCustomFacetsQueryParams(copyCustoms)
      updateRouterQuery(customFacetsParamsObject).then()
      setCustoms(copyCustoms)
    } else {
      if (isEnabled) {
        const currentCustoms = customs.filter(
          (f) => f.id.toString() !== value.id.toString() && f.group?.toString() !== value.id.toString()
        )
        const params = router.query[value.name] ?? []
        const customFacetsParamsObject = {
          [value.name]: Array.isArray(params) ? params.filter((query) => query !== value.query) : [],
        }
        updateRouterQuery(customFacetsParamsObject).then()
        setCustoms(currentCustoms)
      } else {
        const customFacets = [...customs, value]
        const customFacetsParamsObject = setCustomFacetsQueryParams(customFacets)
        updateRouterQuery(customFacetsParamsObject).then()
        setCustoms(customFacets)
      }
    }
  }

  const resetPriceRange = () => {
    updateRouterQuery()
  }

  const resetAll = () => {
    setCustoms([])
    setBrandsFilters([])
    setMinPrice(null)
    setMaxPrice(null)
    lastSyncedUrlPricesRef.current = { minPrice: null, maxPrice: null }

    isUpdatingUrlRef.current = true

    const { slug, term } = { ...router.query }
    const cleanQuery: Record<string, string | string[] | undefined> = {}
    if (slug) cleanQuery.slug = slug
    if (term) cleanQuery.term = term

    return router
      .replace(
        {
          pathname: router.pathname,
          query: cleanQuery,
        },
        undefined,
        { scroll: false }
      )
      .then(() => {
        setTimeout(() => {
          isUpdatingUrlRef.current = false
        }, 300)
      })
      .catch(() => {
        isUpdatingUrlRef.current = false
      })
  }

  const filtersContextValue: ICategoryFiltersContext = {
    brands: brandsFilters,
    custom: {},
    brandsData,
    getBrandData,
    encodeBrandFilter,
    decodeBrandFilter,
    toggleBrandFilter,
    isBrandFilterEnabled,
    queryParams,
    setCategoryData,
    setCategoryBanners,
    setSortOrder,
    toggleCustom,
    encodeCustomFilter,
    decodeCustomFilter,
    isCustomFilterEnabled,
    customs,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    updatePriceRangeInUrl: debouncedUpdatePriceRangeInUrl,
    resetAll,
    resetPriceRange,
  }

  const { name: categoryName, description: categoryDescription } = id
    ? findCategoryNameAndDescription(categories, parseFloat(id as string))
    : { name: '', description: '' }
  const categoryDetails = categoryDescription ? getCategoryDetails(categoryDescription) : null

  const pageTitle = manufacturerName || categoryName

  return (
    <>
      {categoryDetails ? (
        <Helmet title={categoryDetails.title} description={categoryDetails.meta_description} type="category" />
      ) : (
        pageTitle && <Helmet title={pageTitle} type="category" />
      )}
      <TranslationsLoaderProvider>
        <CategoryFiltersContext.Provider value={filtersContextValue}>
          {categoryDetails?.h1 && <h1 style={{ display: 'none' }}>{categoryDetails.h1}</h1>}
          {categoryDetails?.h2 && <h2 style={{ display: 'none' }}>{categoryDetails.h2}</h2>}
          <ModalStateProvider>
            <CategoryView
              banners={banners}
              categoryData={category ?? undefined}
              currentPage={+(router.query.page ?? '1')}
              categoryNameOrSearchTerm={categoryName ? categoryName : searchTerm}
              categoryName={categoryName}
              categoryDescription={categoryDetails?.description}
              catalogGroups={catalogGroups}
              manufacturerId={manufacturerId}
              manufacturerIds={manufacturerIds}
              manufacturerName={manufacturerName}
              jsxData={jsxData}
            />
          </ModalStateProvider>
        </CategoryFiltersContext.Provider>
      </TranslationsLoaderProvider>
    </>
  )
}

export default CategoryPage
